import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

interface ChangeOrderWarningDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onApproveChangeOrder: () => void;
  changeOrderCount: number;
}

const ChangeOrderWarningDialog: React.FC<ChangeOrderWarningDialogProps> = ({
  isOpen,
  onClose,
  onContinue,
  onApproveChangeOrder,
  changeOrderCount
}) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);

  const handleContinue = async () => {
    setIsContinuing(true);
    try {
      await onContinue();
    } finally {
      setIsContinuing(false);
    }
  };

  const handleApproveChangeOrder = async () => {
    setIsApproving(true);
    try {
      await onApproveChangeOrder();
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Pending Change Order Detected
          </AlertDialogTitle>
          <AlertDialogDescription>
            There {changeOrderCount === 1 ? 'is' : 'are'} {changeOrderCount} pending change order{changeOrderCount === 1 ? '' : 's'} 
            that {changeOrderCount === 1 ? 'has' : 'have'} been sent to the customer but not yet approved. 
            
            <br /><br />
            
            You can either:
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Approve the change order(s) first (recommended)</li>
              <li>Continue creating the invoice anyway</li>
            </ul>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex gap-2">
          <AlertDialogCancel onClick={onClose}>
            Cancel
          </AlertDialogCancel>
          <Button 
            onClick={handleContinue}
            disabled={isContinuing || isApproving}
            variant="secondary"
          >
            {isContinuing ? 'Processing...' : 'Continue Anyway'}
          </Button>
          <Button 
            onClick={handleApproveChangeOrder}
            disabled={isContinuing || isApproving}
          >
            {isApproving ? 'Approving...' : `Approve Change Order${changeOrderCount === 1 ? '' : 's'} First`}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ChangeOrderWarningDialog;